import { useMemo } from 'react';
import { synthesizeWorkspaceMeta } from '../__mocks__/workspaceSelector.mock.js';

const STORAGE_KEY = 'aims.lastVisited';

/**
 * Resolves the "last visited" record for the resume banner.
 *
 * Source of truth, mock-driven for V1 and kept behind this hook so the backend
 * can replace it without touching the banner:
 *
 *   1. `localStorage['aims.lastVisited']` = `{ workspaceId, studioId, at }` —
 *      a follow-up has each studio write this on entry.
 *   2. Fallback: the active workspace plus its synthesized last-visited studio.
 *
 * Returns `null` when there is neither a stored record nor an active workspace
 * (a first-ever login), so the banner hides itself.
 *
 * @param {{ id: string, name: string }|null} active — the ACTIVE workspace,
 *   resolved against the unfiltered list: a search must not change what
 *   "where you left off" means.
 * @param {Array<{ id: string, name: string }>} tenants — unfiltered list.
 */
export function useLastVisited(active, tenants) {
  return useMemo(() => {
    let stored = null;
    try {
      stored = JSON.parse(globalThis.localStorage?.getItem(STORAGE_KEY) || 'null');
    } catch {
      // Corrupt or unreadable (private mode) — fall through to the mock.
      stored = null;
    }

    if (stored?.workspaceId && stored?.studioId) {
      const ws = tenants.find((t) => t.id === stored.workspaceId);
      // Only trust a stored record for a workspace the user still has access
      // to; otherwise the banner would offer a dead end.
      if (ws) {
        return {
          workspaceId: ws.id,
          workspaceName: ws.name,
          studioId: stored.studioId,
          // The time label has to describe THAT workspace. Reading it off the
          // active workspace's meta (as this did before) printed an unrelated
          // workspace's "2h ago" next to a different workspace's name.
          timeLabel: stored.timeLabel || synthesizeWorkspaceMeta(ws).lastVisitedLabel,
        };
      }
    }

    if (!active) return null;
    const activeMeta = synthesizeWorkspaceMeta(active, { isActive: true });
    if (!activeMeta.lastVisitedStudioId) return null;

    return {
      workspaceId: active.id,
      workspaceName: active.name,
      studioId: activeMeta.lastVisitedStudioId,
      timeLabel: activeMeta.lastVisitedLabel,
    };
  }, [active, tenants]);
}

export default useLastVisited;
